<template>
  <!-- Deposit Modal -->
  <v-dialog
    :value="value"
    max-width="500"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card class="rounded-lg">
      <!-- Modal Header -->
      <div
        class="flex items-center justify-between p-6 border-b border-gray-200"
      >
        <h3 class="text-lg font-semibold text-gray-900">Select Asset</h3>
        <v-btn icon small @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Deposit Type Tabs -->
      <div
        v-if="!(actionText.t1 === actionText.t2)"
        class="flex p-4 border-b border-gray-200"
      >
        <button
          v-if="actionText.t1"
          class="flex-1 py-3 px-4 text-sm font-medium rounded-lg mr-2 transition-colors"
          :class="
            localModalType === 'crypto'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          "
          @click="localModalType = 'crypto'"
        >
          {{ actionText.t1 }}
        </button>
        <button
          v-if="showFiatTab"
          class="flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-colors"
          :class="
            localModalType === 'fiat'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          "
          @click="localModalType = 'fiat'"
        >
          {{ actionText.t2 }}
        </button>
      </div>

      <!-- Search Bar -->
      <div class="p-4 border-b border-gray-200">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Select Asset"
            class="w-full pl-4 pr-10 py-3 border border-solid border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
          <v-icon
            class="transform -translate-y-1/2 text-gray-400"
            style="
              right: 10px;
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
            "
          >
            mdi-magnify
          </v-icon>
        </div>
      </div>

      <!-- Asset List -->
      <div class="max-h-80 overflow-y-auto">
        <!-- Loading State -->
        <div v-if="loadingAssets" class="flex items-center justify-center py-8">
          <v-progress-circular
            indeterminate
            color="yellow"
          ></v-progress-circular>
          <span class="ml-3 text-gray-500">Loading assets...</span>
        </div>

        <!-- Asset List -->
        <template v-else>
          <template v-if="localModalType === 'crypto'">
            <div
              v-for="asset in filteredAssets"
              :key="asset.symbol"
              class="flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
              @click="handleAssetClick(asset)"
            >
              <div class="flex items-center w-full">
                <div class="w-10 h-10 rounded-full mr-4 overflow-hidden">
                  <img
                    v-if="asset.logo"
                    :src="asset.logo"
                    :alt="asset.symbol"
                    class="w-10 h-10 object-cover"
                    @error="$event.target.style.display = 'none'"
                  />
                  <div
                    v-else
                    class="w-full h-full bg-orange-500 flex items-center justify-center"
                  >
                    <!-- <span class="text-white font-bold text-sm">{{
                    asset.symbol.charAt(0)
                  }}</span> -->
                  </div>
                </div>

                <div>
                  <div class="font-medium text-gray-900">
                    {{ asset.symbol }}
                  </div>
                  <div class="text-sm text-gray-500">{{ asset.name }}</div>
                </div>
              </div>
              <div
                v-if="asset.percentageChange !== undefined"
                class="text-right w-full"
              >
                <div
                  :class="{
                    'text-green-500': asset.percentageChange > 0,
                    'text-red-500': asset.percentageChange < 0,
                    'text-gray-400': asset.percentageChange === 0
                  }"
                  class="text-xs font-medium"
                >
                  {{ asset.percentageChange > 0 ? '' : ''
                  }}{{ asset.percentageChange }}%
                </div>
                <div v-if="asset.price" class="text-xs text-gray-400">
                  ${{ Number(asset.price).toLocaleString() }}
                </div>
              </div>
            </div>

            <!-- No Results -->
            <div
              v-if="filteredAssets.length === 0"
              class="flex flex-col items-center justify-center py-8 text-gray-500"
            >
              <v-icon class="mb-2" color="gray">mdi-magnify</v-icon>
              <span>No assets found</span>
            </div>
          </template>
          <template v-if="localModalType === 'fiat'">
            <div
              v-for="asset in filteredFiatAssets"
              :key="asset.symbol"
              class="flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
              @click="handleAssetClick(asset)"
            >
              <div class="flex items-center w-full">
                <div class="w-10 h-10 rounded-full mr-4 overflow-hidden">
                  <img
                    :src="asset.logo"
                    :alt="asset.symbol"
                    class="w-10 h-10 object-cover"
                  />
                </div>
                <div>
                  <div class="font-medium text-gray-900">
                    {{ asset.symbol }}
                  </div>
                  <div class="text-sm text-gray-500">{{ asset.name }}</div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import cryptocoin from '../api/users/cryptocoin'

export default {
  name: 'DepositModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: Object,
      required: true,
      validator: (obj) => obj.t1 && obj.t2
    },
    modalType: {
      type: String,
      default: 'crypto',
      validator: (value) => ['crypto', 'fiat'].includes(value)
    },
    dataToUse: {
      type: String,
      default: 'assets',
      validator: (value) => ['assets', 'wallets'].includes(value)
    }
  },

  data() {
    return {
      searchQuery: '',
      localModalType: this.modalType,
      cryptoAssets: [],
      cryptoWallets: [],
      loadingAssets: false,
      fiatAssets: [
        {
          symbol: 'NGNX',
          name: 'Nigerian Naira',
          logo:
            'https://cdn3d.iconscout.com/3d/premium/thumb/nigerian-naira-3d-illustration-download-in-png-blend-fbx-gltf-file-formats--financial-money-currency-symbol-pack-business-illustrations-3865857.png'
        }
      ]
    }
  },

  computed: {
    // The only fiat asset is naira (NGNX), and the flows behind it - virtual
    // account funding, naira payouts - are Nigeria-only, so Ghana/Kenya users
    // must not see the tab at all. Mirrors the mobile app's AssetSheet.
    showFiatTab() {
      return !!this.actionText.t2 && this.userCountry === 'Nigeria'
    },
    filteredAssets() {
      if (!this.searchQuery && this.dataToUse === 'assets') {
        return this.cryptoAssets
      }

      if (!this.searchQuery && this.dataToUse === 'wallets') {
        return this.walletAssets
      }

      if (this.dataToUse === 'assets' && this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        return this.cryptoAssets.filter(
          (asset) =>
            asset.symbol.toLowerCase().includes(query) ||
            asset.name.toLowerCase().includes(query)
        )
      }

      if (this.dataToUse === 'wallets' && this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        return this.walletAssets.filter(
          (wallet) =>
            wallet.coin?.name.toLowerCase().includes(query) ||
            wallet.coin?.symbol.toLowerCase().includes(query)
        )
      }

      return []
    },
    walletAssets() {
      if (this.cryptoWallets.length > 0) {
        // `GET /crypto-wallets` (getUserCryptoWallets on the backend) auto-
        // creates and returns a wallet row for *every* Currency document in
        // the system - including NGNX and any other manual/no-rate currency
        // - not just the ones with a live Quidax market rate. The mobile
        // app never has this problem because its swap/asset pickers are
        // built from the crypto-rates list first (mapCryptoRatesToAssets in
        // snappy-app/utils/cryptoRates.ts) and only use wallets to attach a
        // balance - wallets are never the source list. Match that here: a
        // wallet only becomes a pickable asset if it also has a matching
        // live-rate entry in `cryptoAssets`. Without this, picking any coin
        // that only exists as an auto-created wallet (no rate) sent users to
        // `/user/products/crypto/trade/undefined`, because there was never a
        // `coinId` to find on it - `wallet.coin` only carries the wallet's
        // own `_id`, not a `coinId` field.
        const value = this.cryptoWallets
          .map((wallet) => {
            const asset = this.cryptoAssets.find(
              (asset) => asset.coinId === wallet.coin?._id
            )
            return { wallet, asset }
          })
          .filter(({ asset }) => asset !== undefined)
          .map(({ wallet, asset }) => ({
            ...wallet,
            ...asset,
            balance: wallet.balance
          }))
        return value
      }
      return []
    },
    otherAssets() {
      return this.cryptoWallets
        .filter(
          (wallet) =>
            !this.cryptoAssets.find(
              (asset) => asset.coinId === wallet.coin?._id
            )
        )
        .map((wallet) => ({
          ...wallet,
          ...this.cryptoAssets.find(
            (asset) => asset.coinId === wallet.coin?._id
          )
        }))
    },
    filteredFiatAssets() {
      if (!this.searchQuery) {
        return this.fiatAssets
      }
      return this.fiatAssets.filter((asset) =>
        asset.symbol.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    }
  },

  watch: {
    modalType(newVal) {
      this.localModalType = newVal
    },
    localModalType(newVal) {
      this.$emit('deposit-type-changed', newVal)
    },
    // A caller can open straight onto the fiat tab; without this a non-Nigerian
    // user would land on a tab that no longer has a button to leave.
    showFiatTab: {
      immediate: true,
      handler(canShow) {
        if (!canShow && this.localModalType === 'fiat') {
          this.localModalType = 'crypto'
        }
      }
    }
  },

  methods: {
    closeModal() {
      this.searchQuery = ''
      this.$emit('input', false)
      this.$emit('close')
    },
    async getCryptoAssets() {
      try {
        this.loadingAssets = true
        const [ratesRes, walletsRes] = await Promise.all([
          cryptocoin.cryptoCoin().getCryptosRate(),
          cryptocoin.cryptoCoin().getCryptosWallets()
        ])
        this.cryptoWallets = walletsRes.data.data || []
        const wallets = this.cryptoWallets
        this.cryptoAssets = (ratesRes.data.data || []).map((asset) => {
          const wallet = wallets.find((w) => w.coin._id === asset.coinId)
          return {
            symbol: asset.symbol,
            name: asset.currency,
            logo: asset.logo,
            price: asset.price,
            percentageChange: asset.percentageChange,
            coinId: asset.coinId,
            hasAutoProcessing: wallet?.coin?.hasAutoProcessing ?? false
          }
        })
      } catch (error) {
        console.log('Error fetching crypto assets:', error)
      } finally {
        this.loadingAssets = false
      }
    },
    async getCryptoWallets() {
      const res = await cryptocoin.cryptoCoin().getCryptosWallets()
      this.cryptoWallets = res.data.data
    },
    handleAssetClick(asset) {
      this.$emit('asset-clicked', asset, this.localModalType)
      this.closeModal()
    }
  },
  mounted() {
    this.getCryptoAssets()
  }
}
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>
